import { Box, HStack, VStack, Menu, Pressable, Checkbox } from 'native-base';
import { useTranslation } from 'react-i18next';

import { FilterLinesIcon, Text } from '@pimm/base';

export type StatusFilter = {
  key: string;
  label: string;
  dot: string;
  border?: string;
  dashed?: boolean;
};

const STATUS_FILTERS: StatusFilter[] = [
  { key: 'completed', label: 'cleaning_task:completed', dot: 'blue.500' },
  { key: 'assigned', label: 'cleaning_task:assigned', dot: 'yellow.500' },
  { key: 'unassigned', label: 'cleaning_task:unassigned', dot: 'yellow.200', border: 'yellow.500' },
  { key: 'unscheduled', label: 'cleaning_task:unscheduled', dot: 'gray.300', dashed: true },
  { key: 'na', label: 'cleaning_task:n_a', dot: 'gray.500' },
];

type PopoverFilterMenuProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  activeStatusFilters: Set<string>;
  onToggleStatusFilter: (key: string) => void;
  onClearFilters: () => void;
};

export const PopoverFilterMenu = ({
  isOpen,
  onOpen,
  onClose,
  activeStatusFilters,
  onToggleStatusFilter,
  onClearFilters,
}: PopoverFilterMenuProps) => {
  const { t } = useTranslation();
  const filterCount = activeStatusFilters.size;

  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom right"
      trigger={triggerProps => (
        <Pressable {...triggerProps}>
          <Box
            w={8}
            h={8}
            borderWidth={1}
            borderColor={filterCount > 0 ? 'primary.400' : 'gray.200'}
            bg={filterCount > 0 ? 'primary.50' : 'white'}
            borderRadius="md"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <FilterLinesIcon size="16px" color={filterCount > 0 ? 'primary.500' : 'gray.700'} />
            {filterCount > 0 && (
              <Box
                position="absolute"
                top={-1}
                right={-1}
                minW={4}
                h={4}
                bg="primary.400"
                borderRadius="sm"
                alignItems="center"
                justifyContent="center"
                borderWidth={1.5}
                borderColor="white"
              >
                <Text size="2xs" fontWeight={700} color="gray.900">
                  {filterCount}
                </Text>
              </Box>
            )}
          </Box>
        </Pressable>
      )}
    >
      <VStack py={2} px={3} space={1} minW="200px">
        <Text size="sm" fontWeight={700} color="gray.600" textTransform="uppercase">
          {t('cleaning_task:task_status')}
        </Text>

        {STATUS_FILTERS.map(f => (
          <Pressable key={f.key} onPress={() => onToggleStatusFilter(f.key)}>
            <HStack alignItems="center" space={2} py={1.5}>
              <Checkbox
                isChecked={activeStatusFilters.has(f.key)}
                value={f.key}
                accessibilityLabel={f.label}
                colorScheme="primary"
                size="sm"
                borderWidth={1}
                borderRadius="sm"
                _checked={{ bg: 'primary.400', borderColor: 'primary.400' }}
                _unchecked={{ bg: 'white', borderColor: 'gray.300' }}
                mr={1}
              />
              <Text size="md" fontWeight={500} color="gray.700" flex={1}>
                {t(f.label)}
              </Text>
            </HStack>
          </Pressable>
        ))}
        {filterCount > 0 && (
          <Pressable onPress={onClearFilters} borderTopWidth={1} borderColor="gray.100" pt={4}>
            <Text size="md" fontWeight={500} color="red.700" textAlign="right">
              {t('cleaning_task:clear_all_filters')}
            </Text>
          </Pressable>
        )}
      </VStack>
    </Menu>
  );
};

export default PopoverFilterMenu;
